/**
 * useOpenRouterParams Hook
 *
 * Fetches OpenRouter model metadata (supported_parameters, defaults, limits)
 * from our proxy endpoint. Cached in-memory on the client.
 */

import { useState, useEffect } from 'react'
import { apiCall } from '@/lib/api/api-helpers'
import { lookupOpenRouterMeta, type OpenRouterModelMeta } from '@/lib/ai/openrouter-params'

// Client-side cache (shared across all hook instances) with TTL
const META_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes
let metaCache: Record<string, OpenRouterModelMeta> | null = null
let metaCacheTimestamp = 0
let metaFetchPromise: Promise<Record<string, OpenRouterModelMeta>> | null = null

async function fetchOpenRouterMeta(): Promise<Record<string, OpenRouterModelMeta>> {
  if (metaCache && Date.now() - metaCacheTimestamp < META_CACHE_TTL_MS) return metaCache

  // Reset cache if expired
  if (metaCache && Date.now() - metaCacheTimestamp >= META_CACHE_TTL_MS) {
    metaCache = null
    metaFetchPromise = null
  }

  if (metaFetchPromise) return metaFetchPromise

  metaFetchPromise = (async () => {
    try {
      const result = await apiCall('/api/model-management/openrouter-meta', true) as { data: Record<string, OpenRouterModelMeta> }
      metaCache = result.data
      metaCacheTimestamp = Date.now()
      return metaCache
    } catch {
      metaFetchPromise = null
      return {}
    }
  })()

  return metaFetchPromise
}

/**
 * Eagerly start fetching the OpenRouter metadata blob.
 * Call this as early as possible for text nodes — the fetch is model-independent.
 * The blob is cached, so subsequent calls are instant.
 */
export function prefetchOpenRouterMeta(): void {
  fetchOpenRouterMeta()
}

export function useOpenRouterParams(modelId: string | undefined) {
  const [result, setResult] = useState<{ modelId: string; map: Record<string, OpenRouterModelMeta> } | null>(null)

  useEffect(() => {
    if (!modelId) return
    let cancelled = false
    fetchOpenRouterMeta().then((allMeta) => {
      if (!cancelled) setResult({ modelId, map: allMeta })
    })
    return () => { cancelled = true }
  }, [modelId])

  const meta = modelId && result?.modelId === modelId ? lookupOpenRouterMeta(result.map, modelId) : null
  return { meta, isLoading: Boolean(modelId && result?.modelId !== modelId) }
}
